import type { QuoteCartQuote } from '@/components/quotes/quoteCartItems'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import { COVERAGE_LABELS } from './checkoutData'

const WHOLE_DOLLARS = { minimumFractionDigits: 0, maximumFractionDigits: 0 }

/** QuoteCartQuoteRow with a Carrier estimate badge beside the carrier name. */
export function CheckoutCartRow({
  quote,
  isCarrierEstimate,
  firstPayment,
  onRemove,
}: {
  quote: QuoteCartQuote
  isCarrierEstimate: boolean
  /** The chosen plan's down payment; unset until a plan is picked. */
  firstPayment?: number
  onRemove: () => void
}) {
  // A carrier estimate's numbers can still move, so they read lighter and approximate.
  const amountClass = cn('font-semibold', isCarrierEstimate ? 'text-ink-400' : 'text-foreground')
  const money = (amount: number) => `${isCarrierEstimate ? '~' : ''}${formatCurrency(amount, WHOLE_DOLLARS)}`

  return (
    <li className="border-ink-100 flex flex-col gap-3 border-b pb-4 last:border-b-0 last:pb-0">
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="flex items-center gap-2">
            <span className="text-foreground truncate font-semibold">{quote.carrierName}</span>
            {isCarrierEstimate && <Badge variant="outline">Carrier estimate</Badge>}
          </span>
          {quote.carrierProgram && <span className="text-ink-400 text-xs">{quote.carrierProgram}</span>}
        </div>
        <Button
          variant="ghost"
          size="xs"
          onClick={onRemove}
          aria-label={`Remove ${quote.carrierName} from cart`}
          className="text-red-700 hover:bg-red-50 hover:text-red-800"
        >
          Remove
        </Button>
      </div>
      <dl className="flex flex-col gap-1.5">
        {quote.coverages.map((coverage) => (
          <div key={coverage.type} className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-ink-500">{COVERAGE_LABELS[coverage.type]}</dt>
            <dd className={amountClass}>
              {coverage.premium === null ? 'Included' : money(coverage.premium)}
            </dd>
          </div>
        ))}
        {quote.oneTimeFees.map((fee) => (
          <div key={fee.id} className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-ink-500">{fee.label}</dt>
            <dd className={amountClass}>{money(fee.amount)}</dd>
          </div>
        ))}
        {firstPayment !== undefined && (
          <div className="border-ink-100 mt-1 flex items-baseline justify-between gap-3 border-t pt-2 text-sm">
            <dt className="text-foreground font-semibold">First payment</dt>
            <dd className="text-foreground font-semibold">{formatCurrency(firstPayment, WHOLE_DOLLARS)}</dd>
          </div>
        )}
      </dl>
    </li>
  )
}
